import React, { useCallback, useState } from 'react';
import { isEmpty, first, result } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Label, Stack, Checkbox } from '@fluentui/react';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UrlInputFluent as UrlInputFluentV1, getUrlPrefixAndDestination } from '@bingads-webui-campaign-react/url-input-fluent';
import { UrlInputFluent as UrlInputFluentV2 } from '@bingads-webui-campaign-react/url-input-fluent-v2';
import { withFluentSwitcher } from '@bingads-webui-react/fluent-version-switcher';

import { getStyles } from './style';

const MobileUrlKey = 'MobileFinalUrls';

const UrlInputFluent = withFluentSwitcher(UrlInputFluentV1, UrlInputFluentV2);

const MobileUrlWithCustomValidation = ({
  validationProps,
  updateValidation,
  i18n,
  helpTopicId,
}) => {
  const { data } = validationProps;

  const mobileFinalUrls = result(data, MobileUrlKey, [undefined]);
  const mobileFinalUrl = first(mobileFinalUrls);

  const [isMobileUrlChecked, setIsMobileUrlChecked] = useState(!!mobileFinalUrl);
  const [cachedValue, setCachedValue] = useState(mobileFinalUrl);
  const [cachedErrors, setCachedErrors] = useState([]);

  const onCheckboxChange = useCallback((ev, checked) => {
    setIsMobileUrlChecked(checked);

    if (!checked) {
      setCachedValue(mobileFinalUrl);
      const [, urlWithoutPrefix] = getUrlPrefixAndDestination(mobileFinalUrl);
      updateValidation([], { [MobileUrlKey]: [] }, !isEmpty(urlWithoutPrefix));
    } else {
      // If the user never touched the Mobile Final URL fields and only flipping the checkbox,
      // we will not be setting isDirty. It is mostly important for the scenario where it initial value
      // of the URL is empty - this will avoid showing the validation error when checkbox is checked.
      const [, urlWithoutPrefix] = getUrlPrefixAndDestination(cachedValue);
      updateValidation(cachedErrors, { [MobileUrlKey]: [cachedValue] }, !isEmpty(urlWithoutPrefix));
    }
  }, [cachedErrors, cachedValue, mobileFinalUrl, updateValidation]);


  const onInputChange = useCallback((errors, value) => {
    if (mobileFinalUrl !== value) {
      updateValidation(errors, { [MobileUrlKey]: [value] });
      setCachedErrors(errors);
    }
  }, [mobileFinalUrl, updateValidation]);

  const classes = useDefaultStyles(getStyles);

  return (
    <div className={classNames('mobile-url-container', classes.MobileUrlContainer)}>
      <Checkbox
        label={i18n.getString(_TL_('Use a different final URL for mobile'))}
        checked={isMobileUrlChecked}
        onChange={onCheckboxChange}
      />
      {isMobileUrlChecked && (
      <React.Fragment>
        <Label className="mobile-final-url-label">
          <Stack horizontal verticalAlign="center" >
            {i18n.getString(_TL_('Mobile URL'))}
            <HelpPopupInline topic={helpTopicId} />
          </Stack>
        </Label>
        <UrlInputFluent
          containerClassName="mobile-final-url"
          url={mobileFinalUrl}
          onChange={onInputChange}
          i18n={i18n}
          dropdownOptionAriaLabel={i18n.getString(_TL_('Mobile URL'))}
          textFieldAriaLabel={i18n.getString(_TL_('Mobile URL'))}
        />
      </React.Fragment>)}
    </div>
  );
};

MobileUrlWithCustomValidation.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      MobileFinalUrls: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  helpTopicId: PropTypes.string,
};

MobileUrlWithCustomValidation.defaultProps = {
  helpTopicId: 'pop_BA_NewCampaignCreate_Ad_MobileURL',
};

export const ValidatedMobileUrl = withError(withCustomValidation(MobileUrlWithCustomValidation));
